import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {UnmountClosed} from 'react-collapse';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {Alert, Button, DatePicker, Form, Space} from 'antd';
import {StyledExportModal, StyledExportModalBlockedMessage} from './StyledExportModal';
import RadioGroupIcon from '../../../RadioGroupIcon';
import SpinSmall from '../../../SpinSmall';
import {FileCsvIcon, FilePdfIcon, FileZipIcon} from '../../../../icons';
import {ReactComponent as ExportSvgImage} from '../../../../static/images/pages/transactions/export-transactions.svg';
import {dateHelpers, objectHelpers} from '../../../../utils/helpers';

const {Item} = Form;

const baseDate = dayjs().subtract(1, 'month');
const endOfPreviousMonth = baseDate.endOf('month');

const defaultFormValues = {
  format: 'pdf',
  date: baseDate,
  date_range: []
};

const pickerProps = {
  size: 'middle'
}

const isFormatBlocked = (format, blockedFormat) => Boolean(blockedFormat) && Boolean(format) && blockedFormat === format;

const ExportModal = ({
  blockedFormat,
  enableFormatField = true,
  error,
  handleCancel,
  handleOk,
  loading = false,
  open = false,
  options = ['csv', 'pdf', 'zip'],
  title,
  ...rest
}) => {
  const [form] = Form.useForm();
  const [t] = useTranslation('main');
  const [formValues, setFormValues] = useState(defaultFormValues);

  const format = Form.useWatch('format', form);
  const date = Form.useWatch('date', form);
  const dateRange = Form.useWatch('date_range', form);

  const disabledOkButton = useMemo(() => {
    if (loading || isFormatBlocked(format, blockedFormat)) return true;

    return format === 'pdf'
      ? !objectHelpers.isValidValue(date)
      : !(dateRange && dateRange.length >= 2);
  }, [blockedFormat, date, dateRange, format, loading]);

  const isBlockedFormatSelected = useMemo(() => isFormatBlocked(format, blockedFormat), [blockedFormat, format]);

  const radioOptions = useMemo(() => {
    return [
      {icon: <FilePdfIcon />, label: 'PDF', value: 'pdf'},
      {icon: <FileCsvIcon />, label: 'Spreadsheet', value: 'csv'},
      {icon: <FileZipIcon />, label: 'ZIP Invoices', value: 'zip'}
    ].filter(option => options.includes(option.value));
  }, [options]);

  useEffect(() => {
    if (options.length > 1 && !options.includes('pdf')) {
      const format = options[0];
      setFormValues({...formValues, format});
      form.setFieldValue('format', format);
    }
  }, [options]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) {
      setFormValues(defaultFormValues);
      form.setFieldsValue(defaultFormValues);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAfterClose = () => {
    setFormValues(defaultFormValues);
    form.setFieldsValue(defaultFormValues);
  }

  const onValuesChange = (values) => {
    setFormValues({
      ...formValues,
      ...values
    });
  }

  const footer = [
    <Button
      key='ok'
      disabled={disabledOkButton}
      onClick={() => (!disabledOkButton && handleOk) && handleOk(formValues)}
      type='primary'
      size='large'
    >
      {t('export')}
    </Button>
  ];

  return (
    <StyledExportModal
      afterClose={handleAfterClose}
      forceRender
      footer={footer}
      onCancel={handleCancel}
      open={open}
      title={title || `${t('export')}`}
      width={488}
      {...rest}
    >
      <SpinSmall spinning={loading}>
        <Space direction='vertical' size='middle'>
          <ExportSvgImage />
          <Form
            form={form}
            initialValues={formValues}
            layout='vertical'
            onValuesChange={onValuesChange}
          >
            {format === 'pdf' ? (
              <Item
                label={t('selectDate')}
                name='date'
              >
                <DatePicker
                  {...pickerProps}
                  disabled={isBlockedFormatSelected}
                  disabledDate={(current) => current.isAfter(endOfPreviousMonth)}
                  format='MMM YYYY'
                  picker='month'
                  placeholder={t('selectDate')}
                />
              </Item>
            ) : (
              <Item
                label={t('selectDate')}
                name='date_range'
              >
                <DatePicker.RangePicker
                  {...pickerProps}
                  disabled={isBlockedFormatSelected}
                  disabledDate={(current) => current.isAfter(dayjs())}
                  panelRender={dateHelpers.getDatePickerPanelRender}
                  format='DD MMM YYYY'
                  superNextIcon={false}
                  superPrevIcon={false}
                />
              </Item>
            )}
            {enableFormatField && (
              <Item
                label={`${t('select')} ${t('format')}`}
                name='format'
                style={{marginBottom: 0}}
              >
                <RadioGroupIcon options={radioOptions} />
              </Item>
            )}
          </Form>
        </Space>
        <UnmountClosed isOpened={isBlockedFormatSelected}>
          <StyledExportModalBlockedMessage>
            <Alert message={t('noAvailableExportData')} />
          </StyledExportModalBlockedMessage>
        </UnmountClosed>
        <UnmountClosed isOpened={Boolean(error)}>
          <StyledExportModalBlockedMessage>
            <Alert message={error} type='error' showIcon />
          </StyledExportModalBlockedMessage>
        </UnmountClosed>
      </SpinSmall>
    </StyledExportModal>
  );
}

ExportModal.propTypes = {
  blockedFormat: PropTypes.string,
  enableFormatField: PropTypes.bool,
  error: PropTypes.string,
  handleCancel: PropTypes.func,
  handleOk: PropTypes.func,
  loading: PropTypes.bool,
  open: PropTypes.bool,
  options: PropTypes.array
}

export default ExportModal;


